import React, { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import './steps/pro-demo.css'

/**
 * Click-to-play video with a poster frame and a text-forward play affordance
 * (same behavior as the /setting#plan demos: Safari/iOS block autoplay with
 * sound, so playback is user-initiated). Used for the writing/speaking
 * correction clips, which carry the teacher's voice.
 */
const PlayableVideo = ({ src, label, caption }: { src: string; label: string; caption: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // #t=0.1 forces Safari to paint the first frame as a preview
  const previewSrc = src.includes('#') ? src : `${src}#t=0.1`

  const handlePlay = () => {
    const v = videoRef.current
    if (!v) return
    try {
      v.currentTime = 0
    } catch {
      /* ignore */
    }
    v.play()
      .then(() => setIsPlaying(true))
      .catch(() => setIsPlaying(true))
  }

  return (
    <figure className='trial-offer-clip'>
      <div className={`trial-offer-video${isPlaying ? ' is-playing' : ''}`}>
        <video
          ref={videoRef}
          className='trial-offer-video-media'
          src={previewSrc}
          playsInline
          preload='metadata'
          controls={isPlaying}
          onEnded={() => setIsPlaying(false)}
        />
        {!isPlaying && (
          <button type='button' className='trial-offer-video-play' onClick={handlePlay} aria-label={label}>
            <span className='trial-offer-video-play-cue' aria-hidden='true' />
            <span className='trial-offer-video-play-label'>{label}</span>
          </button>
        )}
      </div>
      <figcaption className='trial-offer-clip-caption'>{caption}</figcaption>
    </figure>
  )
}


/**
 * A learner sentence, broken into the pieces the teacher's pen acts on.
 *
 *  - `t`    plain text, left untouched
 *  - `del`  struck through in cinnabar
 *  - `ins`  squeezed in above a caret, the way a correction is written by hand
 */
type Piece = { t?: string; del?: string; ins?: string }

type Mistake = {
  id: string
  /** Chip label — the grammar point, not the sentence. A translation key. */
  label: string
  pieces: Piece[]
  /** The margin note: a translation key, with <zh> tags around the Chinese passed in `noteValues`. */
  note: string
  noteValues: Record<string, string>
}

/**
 * Three mistakes a real beginner makes, each a different shape of correction:
 * a wrong particle swapped, a wrong measure word swapped, and a superfluous
 * verb removed. Kept short enough that the whole sentence fits one line on a
 * phone without wrapping the ruled paper.
 */
const MISTAKES: Mistake[] = [
  {
    id: 'pastTense',
    label: 'onboarding:correctionDemo.mistakes.pastTense.label',
    pieces: [{ t: '我昨天' }, { del: '有' }, { t: '去' }, { ins: '了' }, { t: '图书馆。' }],
    note: 'onboarding:correctionDemo.mistakes.pastTense.note',
    noteValues: { particle: '了', fix: '我昨天去了图书馆' },
  },
  {
    id: 'measureWords',
    label: 'onboarding:correctionDemo.mistakes.measureWords.label',
    pieces: [{ t: '我买了三' }, { del: '个' }, { ins: '本' }, { t: '书。' }],
    note: 'onboarding:correctionDemo.mistakes.measureWords.note',
    noteValues: { word: '本', generic: '个', fix: '三本书' },
  },
  {
    id: 'adjectives',
    label: 'onboarding:correctionDemo.mistakes.adjectives.label',
    pieces: [{ t: '我' }, { del: '是' }, { t: '很高兴。' }],
    note: 'onboarding:correctionDemo.mistakes.adjectives.note',
    noteValues: { verb: '是', adverb: '很', fix: '我很高兴' },
  },
]

// Not copy: the mark beside the margin note, and the teacher's seal (批, "marked").
const CROSS_MARK = '✗'
const SEAL_CHAR = '批'

/**
 * The teacher's red pen on a homework sheet — but the learner drives it.
 *
 * Pick one of three sentences, then press the button to watch it come back
 * marked: the pen strikes the error, squeezes the fix in above a caret, leaves
 * a margin note and stamps the page (批). Choosing a different sentence resets
 * the page to un-marked, so the reveal can be replayed.
 *
 * The marks reuse the styling of the older auto-playing demo; only the trigger
 * changed, from a fixed 14s timeline to the `.is-marked` class this sets.
 */
const CorrectionDemo = () => {
  const { t } = useTranslation('onboarding')
  const [index, setIndex] = useState(0)
  const [marked, setMarked] = useState(false)
  const mistake = MISTAKES[index]

  const choose = (i: number) => {
    setIndex(i)
    setMarked(false)
  }

  return (
    <div className='ob-correction-demo'>
      {/* ---- writing: the homework sheet the reader marks ---- */}
      <figure className='ob-sheet ob-sheet--writing'>
        <figcaption className='ob-sheet-head'>
          <span className='ob-sheet-label'>{t('correctionDemo.yourWriting')}</span>
          <span className='ob-sheet-meta'>
            {marked ? t('correctionDemo.metaMarked') : t('correctionDemo.metaUnmarked')}
          </span>
        </figcaption>

        <div className={`ob-sheet-page ob-sheet--interactive${marked ? ' is-marked' : ''}`}>
          <p className='ob-sheet-line' lang='zh'>
            <span className='ob-typewrite'>
              {mistake.pieces.map((piece, i) => {
                if (piece.del) {
                  return (
                    <span className='ob-mark-del' key={i}>
                      {piece.del}
                    </span>
                  )
                }
                if (piece.ins) {
                  return (
                    <span className='ob-mark-ins' key={i}>
                      <i lang='zh'>{piece.ins}</i>
                      <svg viewBox='0 0 14 8' className='ob-mark-caret'>
                        <path
                          d='M1 7L7 1l6 6'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='1.8'
                          strokeLinecap='round'
                        />
                      </svg>
                    </span>
                  )
                }
                return <React.Fragment key={i}>{piece.t}</React.Fragment>
              })}
            </span>
          </p>

          <p className='ob-sheet-note'>
            <span className='ob-sheet-note-x'>{CROSS_MARK}</span>{' '}
            <Trans t={t} i18nKey={mistake.note} values={mistake.noteValues} components={{ zh: <span lang='zh' /> }} />
          </p>

          <span className='ob-seal'>
            <svg viewBox='0 0 64 64'>
              <circle cx='32' cy='32' r='29' fill='none' stroke='currentColor' strokeWidth='2.5' />
              <circle cx='32' cy='32' r='23.5' fill='none' stroke='currentColor' strokeWidth='1' strokeDasharray='2 3' />
              <text x='32' y='43' textAnchor='middle' fontSize='30' fill='currentColor'>
                {SEAL_CHAR}
              </text>
            </svg>
          </span>
        </div>

        <div className='ob-try'>
          <div className='ob-try-chips' role='tablist' aria-label={t('correctionDemo.pickSentence')}>
            {MISTAKES.map((m, i) => (
              <button
                type='button'
                role='tab'
                aria-selected={i === index}
                key={m.id}
                className={`ob-try-chip${i === index ? ' is-active' : ''}`}
                onClick={() => choose(i)}
              >
                {t(m.label)}
              </button>
            ))}
          </div>
          <button
            type='button'
            className='ob-try-btn'
            onClick={() => setMarked(true)}
            disabled={marked}
            aria-live='polite'
          >
            {marked ? t('correctionDemo.marked') : t('correctionDemo.markIt')}
          </button>
        </div>
      </figure>

      {/* ---- the marked page, recorded ---- */}
      <PlayableVideo
        src='/videos/writingcorrection.mp4'
        label={t('correctionDemo.writingVideo.label')}
        caption={t('correctionDemo.writingVideo.caption')}
      />

      {/* ---- and the half a written page cannot show: how it sounds ---- */}
      <div className='ob-demo-lead'>
        <h3 className='ob-demo-lead-title'>{t('correctionDemo.pronunciationTitle')}</h3>
        <p className='ob-demo-lead-note'>{t('correctionDemo.pronunciationNote')}</p>
      </div>
      <PlayableVideo
        src='/videos/speakingcorrection.mp4'
        label={t('correctionDemo.speakingVideo.label')}
        caption={t('correctionDemo.speakingVideo.caption')}
      />

      {/* ---- what every correction includes ---- */}
      <ul className='ob-prodemo-points'>
        <li>
          <Trans t={t} i18nKey='correctionDemo.points.errors' components={{ mark: <strong className='ob-underline-red' /> }} />
        </li>
        <li>
          <Trans
            t={t}
            i18nKey='correctionDemo.points.phrasing'
            components={{ mark: <strong className='ob-underline-red' /> }}
          />
        </li>
        <li>
          <Trans t={t} i18nKey='correctionDemo.points.tips' components={{ mark: <strong className='ob-underline-green' /> }} />
        </li>
      </ul>
    </div>
  )
}

export default CorrectionDemo
